import { Component, Input, Output, OnInit, OnChanges, SimpleChanges, SimpleChange, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { ComponentBase, Changeable } from 'common';
import { GroupDto, UserDto, UserClass, AccountType, getAccountTypeLabel } from 'UvExplorerServer.Dtos.TS/Auth';
import { UsersService } from '../..';
import { ObjUtils } from '../../../DiscoveryEngine.TS/Utils';


@Component({
	selector: 'group',
	templateUrl: './group.component.html',
	styleUrls: ['./group.component.scss']
})
export class GroupComponent extends ComponentBase implements OnChanges, OnInit, Changeable {

	@Input()
	public target: GroupDto;

	@Input()
	public create: boolean;

	public form: UntypedFormGroup;

	public groupname: UntypedFormControl;
	public description: UntypedFormControl;
	public userClass: UntypedFormControl;
	public active: UntypedFormControl;

	public entries: any[];
	public memberUserGuids: string[];
	public userSelectorVisible: boolean;

	constructor(private fb: UntypedFormBuilder,
		private usersService: UsersService) {

		super();

		this.entries = [];
		this.memberUserGuids = [];
		this.userSelectorVisible = false;

		this.buildForm();
	}

	private buildForm(): void {

		let groupname = "";
		let description = "";
		let userClass = "Regular";
		let active = false;

		if (this.target) {
			groupname = this.target.Name;
			description = this.target.Description;
			userClass = UserClass[this.target.UserClass];
			active = this.target.IsActive;
		}

		this.groupname = new UntypedFormControl(groupname, Validators.required);
		this.description = new UntypedFormControl(description);
		this.userClass = new UntypedFormControl(userClass);
		this.active = new UntypedFormControl(active);

		this.form = this.fb.group({
			groupname: this.groupname,
			description: this.description,
			userClass: this.userClass,
			active: this.active,
		});
	}

	public ngOnInit(): void {

	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.rebuildMemberUserGuids();
		this.rebuildEntries();
		this.buildForm();
	}

	public get hasMemberEntries(): boolean {
		return (!!this.entries && this.entries.length > 0);
	}

	private rebuildMemberUserGuids(): void {
		this.memberUserGuids = [];
		if (this.target) {
			this.target.MemberUserGuids.ForEach((userGuid: string) => {
				this.memberUserGuids.push(userGuid);
			});
		}
	}

	private rebuildEntries(): void {
		this.entries = [];

		if (this.usersService.hasUsers) {
			this.memberUserGuids.forEach((userGuid: string) => {
				let memberUser = this.usersService.users.ToArray().find((user: UserDto) => user.Guid === userGuid);
				if (memberUser) {
					this.addEntry(memberUser);
				}
				else {
					console.error("Could not find group member user: " + userGuid);
				}
			});
		}
	}

	private addEntry(user: UserDto): void {
		let entry = {
			username: user.Username,
			firstname: user.FirstName,
			lastname: user.LastName,
			class: UserClass[user.UserClass],
			type: getAccountTypeLabel(user.AccountType),
			active: (user.IsActive ? 'Yes' : 'No'),
			tag: user
		};

		this.entries.push(entry);
	}

	public removeMemberUser(user: UserDto): void {
		ObjUtils.RemoveArrayElement(this.memberUserGuids, user.Guid);
		this.rebuildEntries();
		this.form.markAsDirty();
	}

	public addMemberUsers(): void {
		this.userSelectorVisible = true;
	}

	public addSelectedMemberUsers(selectedUsers: UserDto[]): void {
		if (!!selectedUsers && selectedUsers.length > 0) {
			selectedUsers.forEach((user: UserDto) => {
				if (!this.memberUserGuids.find((userGuid: string) => userGuid === user.Guid)) {
					this.memberUserGuids.push(user.Guid);
				}
			});

			this.rebuildEntries();
			this.form.markAsDirty();
		}
	}

	public get enableApply(): boolean {
		return (this.tempChanges && this.tempChangesValid);
	}

	public get tempChanges(): boolean {
		return this.form.dirty;
	}

	public get tempChangesValid(): boolean {
		return this.form.valid;
	}

	public get invalidTempChanges(): string[] {
		let result: string[] = [];
		if (!this.form.valid) {
			result.push("Group");
		}
		return result;
	}

	public applyTempChanges(): void {
		if (this.target) {
			this.getChanges(this.target);
			this.buildForm();
		}
	}

	public discardTempChanges(): void {
		this.buildForm();
	}

	private getChanges(group: GroupDto): void {
		group.Name = this.groupname.value;
		group.Description = this.description.value;
		group.UserClass = UserClass[<string>this.userClass.value];
		group.IsActive = this.active.value;

		group.MemberUserGuids.Clear();
		this.memberUserGuids.forEach((userGuid: string) => {
			group.MemberUserGuids.Add(userGuid);
		});
	}

}
